export function friendlyError(message: string | null | undefined): string {
  if (!message) return "Something went wrong. Please try again.";
  const m = message.toLowerCase();

  // Unique-constraint / duplicate-record collisions (most common operator error).
  if (m.includes("duplicate key") && m.includes("barcode")) {
    return "A product with this barcode already exists.";
  }
  if (m.includes("duplicate key") && m.includes("sku")) {
    return "A product with this SKU already exists.";
  }
  if (m.includes("duplicate key")) {
    return "A record with the same details already exists.";
  }
  if (m.includes("violates unique constraint")) {
    return "This would create a duplicate — a record with these details already exists.";
  }

  // Row-level security / authorization.
  if (m.includes("row-level security") || m.includes("permission denied")) {
    return "You don't have permission to perform this action.";
  }

  // Inventory / quantity business rules raised by the trusted RPCs.
  if (m.includes("insufficient stock") || m.includes("not enough stock") || m.includes("exceeds available")) {
    return "There isn't enough stock available to complete this. Check the quantities and try again.";
  }
  if (m.includes("would go negative") || m.includes("negative on-hand") || m.includes("negative stock")) {
    return "This would take stock below zero, which isn't allowed.";
  }
  if (m.includes("quantity invariant")) {
    return "This change would leave the order's quantities inconsistent. Review the line quantities and try again.";
  }
  if (m.includes("already finalized") || m.includes("already completed")) {
    return "This has already been finalized and can no longer be changed.";
  }
  if (m.includes("over-receipt") || m.includes("over receipt") || m.includes("exceeds the allowed") || m.includes("cannot receive more than")) {
    return "This receipt exceeds what's allowed for the order. Reduce the quantity or adjust the over-receipt policy.";
  }
  if (m.includes("cycle") && m.includes("bundle")) {
    return "This would make a bundle that contains itself, which isn't allowed.";
  }
  if (m.includes("check digit") || m.includes("invalid gtin") || m.includes("invalid ean") || m.includes("invalid upc") || m.includes("invalid isbn")) {
    return "That identifier failed its check-digit validation — it may have a typo.";
  }

  // JSON / JSONB contract errors — a malformed payload or a list/scalar shape
  // mismatch must never surface as a raw PostgreSQL type error.
  if (m.includes("invalid input syntax for type json")) {
    return "Some of the data couldn't be saved because it wasn't in a valid format. Review the entry and try again.";
  }
  if (m.includes("cannot extract element")) {
    return "A field expected to hold a list or details is in an unexpected format. Review the entry and try again.";
  }

  // Configuration / connectivity.
  if (m.includes("not configured") || m.includes("credentials required")) {
    return "This connection isn't configured yet. Add your account details to continue.";
  }
  if (m.includes("network") || m.includes("fetch") || m.includes("failed to fetch")) {
    return "Couldn't reach the server. Check your connection and try again.";
  }

  return message;
}

/**
 * A validation failure raised entirely on the client, BEFORE any network call
 * was made. Nothing was sent to the server, so nothing can have been created —
 * unlike an RPC/transport error, whose outcome may be unknown.
 *
 * This distinction matters for create recovery: a local validation failure may
 * release the write-ahead record, while an unknown transport outcome must not.
 */
export class LocalValidationError extends Error {
  readonly localValidation = true;

  constructor(message: string) {
    super(message);
    this.name = "LocalValidationError";
  }
}

/** Structural check so a bundled/reconstructed error still classifies correctly. */
export function isLocalValidationError(error: unknown): boolean {
  if (error instanceof LocalValidationError) return true;
  if (!error || typeof error !== "object") return false;
  return (error as { localValidation?: unknown }).localValidation === true;
}